import type {StoreEconomicsSkuContribution} from '@ozon-ai/ozon/contracts';
import {Card, CardContent, CardFooter} from '@/components/ui/card.tsx';
import {cn} from 'cn';
import {FLOW_VALUE_WIDTH_CLASS, flowAmountClassName, flowRowClassName, formatFlow} from '../unit-economics-page/format-money';

type SkuContributionWaterfallProps = {
	rows: StoreEconomicsSkuContribution[];
	skuLabels: Map<string, string>;
};

function SkuAmount({value, asExpense = false}: {value: number | null; asExpense?: boolean}) {
	return (
		<span
			className={cn(
				FLOW_VALUE_WIDTH_CLASS,
				flowAmountClassName(value, asExpense),
				'text-sm font-medium',
			)}
		>
			{formatFlow(value, asExpense)}
		</span>
	);
}

function sumNullable(values: Array<number | null>): number | null {
	if (values.some((value) => value == null)) return null;
	return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

const COLUMN_HEADER_CLASS = cn(FLOW_VALUE_WIDTH_CLASS, 'text-xs text-muted-foreground');

export function SkuContributionWaterfall({rows, skuLabels}: SkuContributionWaterfallProps) {
	const totalRevenue = rows.reduce((sum, row) => sum + row.revenue, 0);
	const totalExpenses = rows.reduce((sum, row) => sum + row.expenses, 0);
	const totalTax = rows.reduce((sum, row) => sum + row.tax, 0);
	const totalCogs = sumNullable(rows.map((row) => row.cogs));
	const totalProfit = sumNullable(rows.map((row) => row.profit));

	return (
		<Card className="gap-0 overflow-hidden py-0 shadow-sm">
			<div className="overflow-x-auto">
				<div className="min-w-[52rem]">
					<CardContent className="flex flex-col gap-1 p-3">
						<div className="flex items-baseline gap-3 pb-1">
							<div className="min-w-0 flex-1 text-xs text-muted-foreground">товар</div>
							<span className={COLUMN_HEADER_CLASS} title="Продажи, баллы за скидки и программы партнёров">
								выручка
							</span>
							<span
								className={COLUMN_HEADER_CLASS}
								title="Комиссия, эквайринг, логистика, реклама и прочие начисления"
							>
								расходы
							</span>
							<span className={COLUMN_HEADER_CLASS} title="Налог по режиму из настроек">
								налог
							</span>
							<span
								className={COLUMN_HEADER_CLASS}
								title="Цена закупки × (доставлено − возвращено)"
							>
								себестоимость
							</span>
							<span
								className={COLUMN_HEADER_CLASS}
								title="Выручка + расходы − налог − себестоимость"
							>
								прибыль
							</span>
						</div>
						{rows.map((row) => {
							const label = skuLabels.get(row.sku) ?? row.offerId ?? row.sku;
							return (
								<div key={row.sku} className={cn(flowRowClassName(false), 'w-full')}>
									<span className="min-w-0 flex-1 truncate text-[15px]" title={label}>
										{label}
									</span>
									<SkuAmount value={row.revenue}/>
									<SkuAmount value={row.expenses}/>
									<SkuAmount value={row.tax} asExpense/>
									<SkuAmount value={row.cogs} asExpense/>
									<SkuAmount value={row.profit}/>
								</div>
							);
						})}
					</CardContent>
					{rows.length > 0 ? (
						<CardFooter className="flex w-full flex-col items-stretch gap-2 border-t bg-muted/50 p-3">
							<div className={cn(flowRowClassName(false), 'w-full')}>
								<span className="min-w-0 flex-1 text-base font-bold">Итого</span>
								<SkuAmount value={totalRevenue}/>
								<SkuAmount value={totalExpenses}/>
								<SkuAmount value={totalTax} asExpense/>
								<SkuAmount value={totalCogs} asExpense/>
								<SkuAmount value={totalProfit}/>
							</div>
						</CardFooter>
					) : null}
				</div>
			</div>
		</Card>
	);
}
